import { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { GreyLayout } from '@/layouts/GreyLayout';
import { useUserStore } from '@/store/userStore';
import { Preloader } from '@/components/Preloader';
import { Checkbox } from '@/webkit/form/Checkbox';
import { useGetAmlCaseDetail } from '@/query/queries/compliance/useGetAmlCaseDetail';
import { useSubmitAmlCase } from '@/query/queries/compliance/useSubmitAmlCase';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import { uploadFile } from '@/store/services/commonServices';
import { BackButton } from '@/components/BackButton';
import { buildValidationSchema } from './schema';
import Notify from '@/components/Notification';
import { ProcessingLoader } from '@/components/ProcessingLoader';
import { Form } from './form';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const ComplianceFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useUserStore(state => state.accessToken);
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({});
  const { caseId } = useParams();
  const { data, isLoading } = useGetAmlCaseDetail(accessToken ? caseId : undefined);
  const { mutateAsync: submit } = useSubmitAmlCase();

  const validationSchema = useMemo(() => (data ? buildValidationSchema(data, t) : null), [data, t]);

  const initialValues = useMemo(() => {
    if (!data) return { questionnaire: {}, documents: {}, has_accepted: false };

    const questionnaire =
      data.questionnaire?.questions.reduce((acc, item) => {
        acc[item.id] = '';
        return acc;
      }, {}) || {};

    const documents = data.documents?.required_documents.reduce((acc, doc) => {
      acc[doc.document_type] = [];
      return acc;
    }, {}) || {};

    return { questionnaire, documents, has_accepted: false };

  }, [data]);

  const uploadFilesOrFail = async files => {
    const uploaded = [];

    for (const file of files) {
      try {
        const result = await uploadFile(file, { protected: true });
        result?.success && uploaded.push(result.data);
      } catch {
        throw new Error('Upload file error');
      }
    }
    return uploaded;
  };

  const onSubmit = async (values) => {
    setLoading(true);
    setUploadErrors({});

    try {
      const documentEntries = Object.entries(values.documents).filter(
        ([, files]) => files.length > 0,
      );

      const uploadResults = await Promise.allSettled(
        documentEntries.map(async ([type, files]) => {
          const result = await uploadFilesOrFail(files);

          if (result.length !== files.length) {
            throw new Error('Upload file error');
          }

          return {
            document_type: type,
            file_ids: result.map(file => file.id),
          };
        }),
      );

      const failedTypes = uploadResults.reduce((acc, result, index) => {
        if (result.status === 'rejected') {
          acc[documentEntries[index][0]] = t('compliance:page.error_general');
        }
        return acc;
      }, {});

      if (Object.keys(failedTypes).length > 0) {
        setUploadErrors(failedTypes);
        return;
      }

      const uploadedDocuments = uploadResults.map(result => result.value);

      const payload = {
        document_request_id: data.document_request_id,
        questionnaire_answers: Object.keys(values.questionnaire).map(id => ({
          question_id: Number(id),
          answer_text: values.questionnaire[id],
        })),
        documents: uploadedDocuments,
      };

      await submit(payload);
      navigate(APP_LINKS.dashboard);
    } catch {
      Notify.error({ text: t('compliance:page.error_general') });
    } finally {
      setLoading(false);
    }
  }

  if (!accessToken) {
    return <Navigate to={APP_LINKS.signIn} replace state={{ from: location }} />;
  }

  if (isLoading) {
    return (
      <GreyLayout>
        <Preloader style={{ height: '70vh' }} />
      </GreyLayout>
    );
  }

  if (!data) return (
    <GreyLayout>
      <div className={s.content}>
        <div className={s.message_wrapper}>
          <div className={s.message}>{t('compliance:page.invalid_link')}</div>
        </div>
      </div>
    </GreyLayout>
  );

  return (
    <GreyLayout>
      <div className={s.back_wrapper}>
        <div className={s.back_inner}>
          <BackButton
            className={s.back}
            onClick={() => navigate(APP_LINKS.dashboard)}
          />
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnMount
      >
        {({ values, setFieldValue, errors, touched, handleSubmit }) => (
          <form onSubmit={handleSubmit} className={s.page}>
            <div className={s.content}>
              {data?.title && (
                <div className={s.header}>
                  <h1 className="f-hero-h4">{data.title}</h1>
                  {data?.description && (
                    <p className="f-body-regular">{data.description}</p>
                  )}
                </div>
              )}
              <div className={s.form}>
                <Form data={data} uploadErrors={uploadErrors} />
              </div>
            </div>
            <div className={s.footer}>
              <div className={s.footer_inner}>
                <Checkbox
                  id="has_accepted"
                  name="has_accepted"
                  checked={values.has_accepted}
                  onChange={() =>
                    setFieldValue('has_accepted', !values.has_accepted)
                  }
                >
                  <span
                    className={classnames(
                      touched.has_accepted && errors.has_accepted && s.error,
                      'f-body-regular-s',
                    )}
                  >
                    <Trans
                      ns="compliance"
                      i18nKey="page.confirm_text"
                      components={{
                        LinkPrivacyPolicy: (
                          <MyLink
                            href={APP_LINKS.docPrivacyAndPolicy}
                            isExternal
                            underline
                          />
                        ),
                        LinkTermsOfUse: (
                          <MyLink
                            href={APP_LINKS.docTermsOfUse}
                            isExternal
                            underline
                          />
                        ),
                      }}
                    />
                  </span>
                </Checkbox>
                <button
                  type="submit"
                  className={classnames(r.yellow_base, s.btn_submit)}
                  disabled={loading || !values.has_accepted}
                >
                  {t('compliance:page.submit')}
                </button>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <ProcessingLoader loading={loading} />
    </GreyLayout>
  );
};

export default ComplianceFormPage;